import { useEffect, useMemo, useState } from "react";
import { db, secondaryAuth } from "../firebase/config";
import {
  collection,
  doc,
  onSnapshot,
  serverTimestamp,
  updateDoc,
  query,
  orderBy,
  setDoc,
} from "firebase/firestore";
import {
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
} from "firebase/auth";
import { useAuth } from "../context/AuthContext";
import { syncStaffMark } from "../crm/staffMarkApi";
import { fmtDT } from "../crm/dateFormat";

function generateTempPassword() {
  return crypto.randomUUID ? crypto.randomUUID() : `Temp${Date.now()}!A1`;
}

const EMPTY_FORM = {
  name: "",
  email: "",
  mobile: "",
  role: "Viewer",
  status: "PendingActivation",
};

function formatDate(value) {
  return fmtDT(value);
}

// Asks the server to give or remove the staff mark for this login (based on
// the saved role and status). Never throws: returns "" when fine, or the reason.
async function trySyncStaffMark(uid) {
  try {
    await syncStaffMark(uid);
    return "";
  } catch (e) {
    return e?.message || "unknown error";
  }
}

export default function UserManagement() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user) return;

    const q = query(collection(db, "platformUsers"), orderBy("createdOn", "desc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const rows = snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
      setUsers(rows);
      setLoading(false);
    });

    return () => {
      if (typeof unsubscribe === "function") {
        unsubscribe();
      }
    };
  }, [user]);

  const isOwner = useMemo(() => user?.role === "Owner", [user]);
  const isSalesHead = useMemo(() => user?.role === "SalesHead", [user]);

  if (!isOwner && !isSalesHead) {
    return (
      <div style={{ padding: 24, color: "#fff" }}>
        <div style={{ background: "#161821", border: "1px solid #2b2f3d", borderRadius: 12, padding: 24 }}>
          You do not have permission to view user management.
        </div>
      </div>
    );
  }

  // A Sales Head can only see and enable/disable Sales-role accounts —
  // not create users, not edit anyone, not touch Owner/Admin/Viewer rows.
  const visibleUsers = isSalesHead ? users.filter((u) => u.role === "Sales") : users;

  async function handleSave() {
    if (!form.name.trim() || !form.email.trim() || !form.mobile.trim()) {
      setError("Name, email, and mobile are required.");
      return;
    }
    if (form.mobile.trim().length !== 10) {
      setError("Mobile number must be exactly 10 digits.");
      return;
    }

    setSaving(true);
    setError("");
    setMessage("");

    try {
      if (editingId) {
        const ref = doc(db, "platformUsers", editingId);
        await updateDoc(ref, {
          name: form.name.trim(),
          mobile: form.mobile.trim(),
          role: form.role,
          status: form.status,
          updatedOn: serverTimestamp(),
        });
        const markProblem = await trySyncStaffMark(editingId);
        setMessage("User updated successfully." + (markProblem ? " BUT the staff mark could not be updated (" + markProblem + "). Please tell the Owner." : ""));
      } else {
        const normalizedEmail = form.email.trim().toLowerCase();
        const tempPassword = generateTempPassword();
        const finalRole = isSalesHead ? "Sales" : form.role;

        // Create the person's login using the secondary connection,
        // so the Owner's own session stays signed in.
        const credential = await createUserWithEmailAndPassword(
          secondaryAuth,
          normalizedEmail,
          tempPassword
        );
        const newUid = credential.user.uid;

        // Send them the "set your password" email right away.
        await sendPasswordResetEmail(secondaryAuth, normalizedEmail);

        // Sign the secondary connection back out; it was only
        // needed to create the login.
        await signOut(secondaryAuth);

        const profile = {
          name: form.name.trim(),
          email: normalizedEmail,
          mobile: form.mobile.trim(),
          role: finalRole,
          status: "PendingActivation",
          uid: newUid,
          createdOn: serverTimestamp(),
          updatedOn: serverTimestamp(),
          createdBy: user?.uid || "system",
          // Only meaningful for Sales Head accounts — controls whether they
          // earn the extra 2% commission override on their team's revenue.
          // Defaults off; Owner turns it on later from the table.
          commissionOverrideEnabled: false,
        };

        await setDoc(doc(db, "platformUsers", newUid), profile);
        const markProblem = await trySyncStaffMark(newUid);
        setMessage("User created. An email has been sent to them to set their password." + (markProblem ? " BUT the staff mark could not be set (" + markProblem + "). Please tell the Owner." : ""));
      }

      setModalOpen(false);
      setForm(isSalesHead ? { ...EMPTY_FORM, role: "Sales" } : EMPTY_FORM);
      setEditingId(null);
    } catch (e) {
      setError(e?.message || "Unable to save user.");
    } finally {
      setSaving(false);
    }
  }

  async function handleEnableDisable(row) {
    try {
      const nextStatus = row.status === "Active" ? "Disabled" : "Active";
      await updateDoc(doc(db, "platformUsers", row.id), {
        status: nextStatus,
        updatedOn: serverTimestamp(),
      });
      const markProblem = await trySyncStaffMark(row.id);
      if (markProblem) setError("Status changed, but the staff mark could not be updated (" + markProblem + "). Please tell the Owner.");
    } catch (e) {
      setError(e?.message || "Unable to update status.");
    }
  }

  // Owner-only: flips the Sales Head's 2% team commission override on/off.
  async function handleToggleCommissionOverride(row) {
    try {
      await updateDoc(doc(db, "platformUsers", row.id), {
        commissionOverrideEnabled: !row.commissionOverrideEnabled,
        updatedOn: serverTimestamp(),
      });
    } catch (e) {
      setError(e?.message || "Unable to update commission override.");
    }
  }

  function openCreate() {
    setEditingId(null);
    setForm(isSalesHead ? { ...EMPTY_FORM, role: "Sales" } : EMPTY_FORM);
    setError("");
    setMessage("");
    setModalOpen(true);
  }

  function openEdit(row) {
    setEditingId(row.id);
    setForm({
      name: row.name || "",
      email: row.email || "",
      mobile: row.mobile || "",
      role: row.role || "Viewer",
      status: row.status || "Active",
    });
    setError("");
    setMessage("");
    setModalOpen(true);
  }

  return (
    <div style={{ padding: 24, color: "#fff", background: "#0f0f13", minHeight: "100%" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#8b7cff" }}>User Management</div>
          <div style={{ color: "#8f95a8", fontSize: 13, marginTop: 4 }}>
            {isSalesHead ? "Manage your sales team's access." : "Manage platform users and access roles."}
          </div>
        </div>
        {(isOwner || isSalesHead) && (
          <button onClick={openCreate} style={{ background: "#6c63ff", color: "#fff", border: "none", borderRadius: 10, padding: "10px 14px", cursor: "pointer", fontWeight: 600 }}>
            Add User
          </button>
        )}
      </div>

      {message ? <div style={{ marginBottom: 12, background: "rgba(62,207,142,0.16)", border: "1px solid rgba(62,207,142,0.28)", color: "#7ce0ad", padding: "10px 12px", borderRadius: 10 }}>{message}</div> : null}
      {error ? <div style={{ marginBottom: 12, background: "rgba(255,82,82,0.14)", border: "1px solid rgba(255,82,82,0.3)", color: "#ff7b7b", padding: "10px 12px", borderRadius: 10 }}>{error}</div> : null}

      <div style={{ background: "#161821", border: "1px solid #2b2f3d", borderRadius: 14, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 860 }}>
            <thead style={{ background: "#1d2030" }}>
              <tr>
                <th style={{ textAlign: "left", padding: "12px 14px", color: "#8f95a8", fontSize: 12, textTransform: "uppercase" }}>Name</th>
                <th style={{ textAlign: "left", padding: "12px 14px", color: "#8f95a8", fontSize: 12, textTransform: "uppercase" }}>Email</th>
                <th style={{ textAlign: "left", padding: "12px 14px", color: "#8f95a8", fontSize: 12, textTransform: "uppercase" }}>Mobile</th>
                <th style={{ textAlign: "left", padding: "12px 14px", color: "#8f95a8", fontSize: 12, textTransform: "uppercase" }}>Role</th>
                <th style={{ textAlign: "left", padding: "12px 14px", color: "#8f95a8", fontSize: 12, textTransform: "uppercase" }}>Status</th>
                <th style={{ textAlign: "left", padding: "12px 14px", color: "#8f95a8", fontSize: 12, textTransform: "uppercase" }}>Last Login</th>
                <th style={{ textAlign: "left", padding: "12px 14px", color: "#8f95a8", fontSize: 12, textTransform: "uppercase" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" style={{ padding: 18, color: "#8f95a8" }}>Loading users...</td></tr>
              ) : visibleUsers.length === 0 ? (
                <tr><td colSpan="7" style={{ padding: 18, color: "#8f95a8" }}>No users found.</td></tr>
              ) : visibleUsers.map((row) => (
                <tr key={row.id} style={{ borderTop: "1px solid #2b2f3d" }}>
                  <td style={{ padding: "12px 14px" }}>{row.name || "—"}</td>
                  <td style={{ padding: "12px 14px" }}>{row.email || "—"}</td>
                  <td style={{ padding: "12px 14px" }}>{row.mobile || "—"}</td>
                  <td style={{ padding: "12px 14px" }}>{row.role || "Viewer"}</td>
                  <td style={{ padding: "12px 14px" }}>{row.status || "Active"}</td>
                  <td style={{ padding: "12px 14px" }}>{formatDate(row.lastLogin)}</td>
                  <td style={{ padding: "12px 14px" }}>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {isOwner && (
                        <button onClick={() => openEdit(row)} style={{ background: "#303446", color: "#fff", border: "none", borderRadius: 8, padding: "7px 10px", cursor: "pointer" }}>Edit</button>
                      )}
                      <button onClick={() => handleEnableDisable(row)} style={{ background: "#2f6d4f", color: "#fff", border: "none", borderRadius: 8, padding: "7px 10px", cursor: "pointer" }}>{row.status === "Active" ? "Disable" : "Enable"}</button>
                      {isOwner && row.role === "SalesHead" && (
                        <button
                          onClick={() => handleToggleCommissionOverride(row)}
                          title="Give this Sales Head an extra 2% on his team's revenue"
                          style={{
                            background: row.commissionOverrideEnabled ? "#6c4fd6" : "#3a3d4d",
                            color: "#fff",
                            border: "none",
                            borderRadius: 8,
                            padding: "7px 10px",
                            cursor: "pointer",
                          }}
                        >
                          2% Team Bonus: {row.commissionOverrideEnabled ? "ON" : "OFF"}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {modalOpen ? (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{ width: "100%", maxWidth: 520, background: "#161821", border: "1px solid #2b2f3d", borderRadius: 16, padding: 22, color: "#fff" }}>
            <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 10 }}>{editingId ? "Edit User" : "Add User"}</div>
            <div style={{ color: "#8f95a8", fontSize: 13, marginBottom: 16 }}>Create or update a platform user.</div>

            <div style={{ display: "grid", gap: 12 }}>
              <div>
                <label htmlFor="user-name" style={{ display: "block", color: "#8f95a8", marginBottom: 6 }}>Name *</label>
                <input id="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} style={{ width: "100%", background: "#0f0f13", border: "1px solid #2b2f3d", borderRadius: 10, padding: "10px 12px", color: "#fff" }} />
              </div>
              <div>
                <label htmlFor="user-email" style={{ display: "block", color: "#8f95a8", marginBottom: 6 }}>Email *</label>
                <input id="user-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!!editingId} style={{ width: "100%", background: editingId ? "#1a1d28" : "#0f0f13", border: "1px solid #2b2f3d", borderRadius: 10, padding: "10px 12px", color: "#fff" }} />
              </div>
              <div>
                <label htmlFor="user-mobile" style={{ display: "block", color: "#8f95a8", marginBottom: 6 }}>Mobile *</label>
                <input
                  id="user-mobile"
                  value={form.mobile}
                  onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) })}
                  maxLength={10}
                  inputMode="numeric"
                  style={{ width: "100%", background: "#0f0f13", border: "1px solid #2b2f3d", borderRadius: 10, padding: "10px 12px", color: "#fff" }}
                />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label htmlFor="user-role" style={{ display: "block", color: "#8f95a8", marginBottom: 6 }}>Role</label>
                  {isSalesHead && !editingId ? (
                    <input id="user-role" value="Sales" disabled style={{ width: "100%", background: "#1a1d28", border: "1px solid #2b2f3d", borderRadius: 10, padding: "10px 12px", color: "#fff" }} />
                  ) : (
                    <select id="user-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} style={{ width: "100%", background: "#0f0f13", border: "1px solid #2b2f3d", borderRadius: 10, padding: "10px 12px", color: "#fff" }}>
                      <option value="Owner">Owner</option>
                      <option value="Admin">Admin</option>
                      <option value="SalesHead">Sales Head</option>
                      <option value="Sales">Sales</option>
                      <option value="Viewer">Viewer</option>
                    </select>
                  )}
                </div>
                <div>
                  <label htmlFor="user-status" style={{ display: "block", color: "#8f95a8", marginBottom: 6 }}>Status</label>
                  <select id="user-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} style={{ width: "100%", background: "#0f0f13", border: "1px solid #2b2f3d", borderRadius: 10, padding: "10px 12px", color: "#fff" }}>
                    <option value="PendingActivation">Pending Activation</option>
                    <option value="Active">Active</option>
                    <option value="Disabled">Disabled</option>
                  </select>
                </div>
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
              <button onClick={() => setModalOpen(false)} style={{ background: "#303446", color: "#fff", border: "none", borderRadius: 10, padding: "10px 12px", cursor: "pointer" }}>Cancel</button>
              <button onClick={handleSave} disabled={saving} style={{ background: "#6c63ff", color: "#fff", border: "none", borderRadius: 10, padding: "10px 12px", cursor: "pointer", opacity: saving ? 0.7 : 1 }}>
                {saving ? "Saving..." : editingId ? "Save Changes" : "Create User"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}